import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { Icon, Typography } from '@packages/ui/shared';
import { colors } from '@packages/core/styles';
import { SkeletonLoader } from '@web/components/skeleton-loader';

export type VehicleOption = { vehicleId: string; description: string };

type Props = {
    options: VehicleOption[];
    selected: string[];
    allVehicles: boolean;
    clearsSavedSelection: boolean;
    isLoading: boolean;
    onChange: (selected: string[]) => void;
    onAllVehiclesChange: (allVehicles: boolean) => void;
};

const PAGE_SIZE = 25;

const SKELETON_ROW_KEYS = Array.from({ length: 8 }, (_, index) => `vehicle-skeleton-${index}`);

const useStyles = createUseStyles({
    toolbar: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 16,
        marginBottom: 12,
    },
    toggle: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 14,
        color: colors.textDarkPrimary,
        cursor: 'pointer',
    },
    divider: {
        width: 1,
        height: 20,
        backgroundColor: colors.grayThree,
    },
    linkButton: {
        border: 'none',
        background: 'none',
        padding: 0,
        fontSize: 13,
        fontWeight: 500,
        color: colors.blueOne,
        cursor: 'pointer',
        '&:disabled': {
            color: colors.grayFive,
            cursor: 'default',
        },
    },
    count: {
        marginLeft: 'auto',
        fontSize: 13,
        fontWeight: 600,
        color: colors.blueOne,
    },
    search: {
        width: '100%',
        height: 38,
        padding: '0 12px',
        marginBottom: 14,
        border: `1px solid ${colors.grayFive}`,
        borderRadius: 3,
        fontSize: 14,
        '&:focus': {
            outline: 'none',
            border: `2px solid ${colors.blueOne}`,
            padding: '0 11px',
        },
    },
    table: {
        border: `1px solid ${colors.grayThree}`,
        borderRadius: 4,
        maxHeight: 360,
        overflowY: 'auto',
        scrollbarWidth: 'thin',
        scrollbarColor: `${colors.blueTwo} ${colors.grayTwo}`,
    },
    endSentinel: {
        height: 1,
    },
    scrollHint: {
        fontSize: 12,
        color: colors.grayFive,
    },
    headRow: {
        position: 'sticky',
        top: 0,
        zIndex: 1,
        display: 'grid',
        gridTemplateColumns: '108px 1fr',
        gap: 12,
        padding: '10px 14px',
        backgroundColor: colors.grayTwo,
        borderBottom: `1px solid ${colors.grayThree}`,
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: '0.3px',
        color: colors.textDarkSecondary,
    },
    row: {
        display: 'grid',
        gridTemplateColumns: '108px 1fr',
        alignItems: 'center',
        gap: 12,
        width: '100%',
        padding: '10px 14px',
        border: 'none',
        borderBottom: `1px solid ${colors.grayTwo}`,
        backgroundColor: colors.white,
        fontSize: 14,
        color: colors.textDarkPrimary,
        textAlign: 'left',
        cursor: 'pointer',
        '&:last-child': {
            borderBottom: 'none',
        },
        '&:hover': {
            backgroundColor: colors.blueFour,
        },
        '&:disabled': {
            cursor: 'default',
            color: colors.grayFive,
            backgroundColor: colors.grayOne,
        },
    },
    skeletonRow: {
        display: 'grid',
        gridTemplateColumns: '108px 1fr',
        alignItems: 'center',
        gap: 12,
        padding: '10px 14px',
        borderBottom: `1px solid ${colors.grayTwo}`,
        '&:last-child': {
            borderBottom: 'none',
        },
    },
    skeletonBar: {
        marginBottom: 0,
    },
    empty: {
        padding: '28px 14px',
        textAlign: 'center',
        fontSize: 14,
        color: colors.textDarkSecondary,
        backgroundColor: colors.white,
    },
    footer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        marginTop: 12,
        fontSize: 13,
        color: colors.textDarkSecondary,
    },
    allVehiclesNote: {
        marginTop: 12,
        padding: '10px 12px',
        borderRadius: 4,
        backgroundColor: colors.blueFour,
        fontSize: 13,
        color: colors.textDarkPrimary,
    },
    emptyNote: {
        marginTop: 12,
        padding: '10px 12px',
        borderRadius: 4,
        backgroundColor: colors.yellowFour,
        fontSize: 13,
        color: colors.textDarkPrimary,
    },
});

export const AssociateVehiclesPicker: FC<Props> = ({
    options,
    selected,
    allVehicles,
    clearsSavedSelection,
    isLoading,
    onChange,
    onAllVehiclesChange,
}) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const [query, setQuery] = useState('');
    const [selectedOnly, setSelectedOnly] = useState(false);
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
    const listRef = useRef<HTMLDivElement | null>(null);
    const endSentinelRef = useRef<HTMLDivElement | null>(null);

    const selectedIds = useMemo(() => new Set(selected), [selected]);

    const isTicked = useCallback(
        (vehicleId: string) => allVehicles || selectedIds.has(vehicleId),
        [allVehicles, selectedIds]
    );
    const tickedCount = allVehicles ? options.length : selected.length;

    const visible = useMemo(() => {
        const needle = query.trim().toLowerCase();
        return options.filter((option) => {
            if (selectedOnly && !selectedIds.has(option.vehicleId)) {
                return false;
            }
            return needle.length === 0 || option.description.toLowerCase().includes(needle);
        });
    }, [options, query, selectedOnly, selectedIds]);

    const rows = useMemo(() => visible.slice(0, visibleCount), [visible, visibleCount]);
    const hasMore = visibleCount < visible.length;

    useEffect(() => {
        setVisibleCount(PAGE_SIZE);
        if (listRef.current) {
            listRef.current.scrollTop = 0;
        }
    }, [query, selectedOnly, allVehicles]);

    useEffect(() => {
        const list = listRef.current;
        const sentinel = endSentinelRef.current;
        if (!list || !sentinel || !hasMore) {
            return;
        }
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    setVisibleCount((previous) => previous + PAGE_SIZE);
                }
            },
            { root: list, rootMargin: '150px' }
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [hasMore, visibleCount, visible.length]);

    const toggle = useCallback(
        (vehicleId: string) => {
            onChange(selectedIds.has(vehicleId) ? selected.filter((id) => id !== vehicleId) : [...selected, vehicleId]);
        },
        [onChange, selected, selectedIds]
    );

    const selectVisible = useCallback(() => {
        const merged = new Set(selected);
        visible.forEach((option) => merged.add(option.vehicleId));
        onChange([...merged]);
    }, [onChange, selected, visible]);

    const clearVisible = useCallback(() => {
        const dropped = new Set(visible.map((option) => option.vehicleId));
        onChange(selected.filter((id) => !dropped.has(id)));
    }, [onChange, selected, visible]);

    if (isLoading) {
        return (
            <div
                className={classes.table}
                aria-busy
                role="status"
                aria-label={t('stisPublications:associateVehicles.loading', 'Loading vehicles')}
            >
                {SKELETON_ROW_KEYS.map((key) => (
                    <div key={key} className={classes.skeletonRow}>
                        <SkeletonLoader className={classes.skeletonBar} type="icon" />
                        <SkeletonLoader className={classes.skeletonBar} type="text" width="48%" />
                    </div>
                ))}
            </div>
        );
    }

    return (
        <div>
            <div className={classes.toolbar}>
                <label className={classes.toggle}>
                    <input
                        type="checkbox"
                        checked={allVehicles}
                        onChange={(event) => onAllVehiclesChange(event.target.checked)}
                    />
                    {t('stisPublications:associateVehicles.allVehicles', 'All vehicles')}
                </label>

                <span className={classes.divider} />

                <button type="button" className={classes.linkButton} disabled={allVehicles} onClick={selectVisible}>
                    {t('stisPublications:associateVehicles.selectAll', 'Select all')}
                </button>
                <button type="button" className={classes.linkButton} disabled={allVehicles} onClick={clearVisible}>
                    {t('stisPublications:associateVehicles.clear', 'Clear')}
                </button>
                <button
                    type="button"
                    className={classes.linkButton}
                    disabled={allVehicles}
                    onClick={() => setSelectedOnly((previous) => !previous)}
                >
                    {selectedOnly
                        ? t('stisPublications:associateVehicles.showAllRows', 'Show all')
                        : t('stisPublications:associateVehicles.showSelected', 'Show selected only')}
                </button>

                <span className={classes.count}>
                    {t('stisPublications:associateVehicles.selectedCount', {
                        count: tickedCount,
                        total: options.length,
                        defaultValue: '{{count}} of {{total}} selected',
                    })}
                </span>
            </div>

            <input
                className={classes.search}
                value={query}
                disabled={allVehicles}
                placeholder={t(
                    'stisPublications:associateVehicles.search',
                    'Search year, carline, transmission or engine'
                )}
                onChange={(event) => setQuery(event.target.value)}
            />

            <div className={classes.table} ref={listRef}>
                <div className={classes.headRow}>
                    <div>{t('stisPublications:associateVehicles.associated', 'ASSOCIATED')}</div>
                    <div>{t('stisPublications:associateVehicles.vehicleType', 'VEHICLE TYPE')}</div>
                </div>

                {rows.length === 0 ? (
                    <div className={classes.empty}>
                        {t('stisPublications:associateVehicles.noMatches', 'No vehicles match')}
                    </div>
                ) : (
                    rows.map((option) => (
                        <button
                            key={option.vehicleId}
                            type="button"
                            role="checkbox"
                            aria-checked={isTicked(option.vehicleId)}
                            disabled={allVehicles}
                            className={classes.row}
                            onClick={() => toggle(option.vehicleId)}
                        >
                            <Icon
                                color="blueOne"
                                name={isTicked(option.vehicleId) ? 'checkbox-marked' : 'checkbox-unmarked'}
                            />
                            <span>{option.description}</span>
                        </button>
                    ))
                )}
                {hasMore && <div ref={endSentinelRef} className={classes.endSentinel} />}
            </div>

            <div className={classes.footer}>
                <span>
                    {t('stisPublications:associateVehicles.showing', {
                        shown: rows.length,
                        total: visible.length,
                        defaultValue: 'Showing {{shown}} of {{total}}',
                    })}
                </span>
                {hasMore && (
                    <span className={classes.scrollHint}>
                        {t('stisPublications:associateVehicles.scrollForMore', 'Scroll for more')}
                    </span>
                )}
            </div>

            {/* Saving an empty selection is allowed, so say what it does before it is pressed. */}
            {clearsSavedSelection && (
                <div className={classes.emptyNote}>
                    <Typography variant="caption2">
                        {t(
                            'stisPublications:associateVehicles.noneSelectedNote',
                            'No vehicles are selected. Saving now takes this publication off every vehicle, so it will not appear in a VIN session.'
                        )}
                    </Typography>
                </div>
            )}

            {allVehicles && (
                <div className={classes.allVehiclesNote}>
                    <Typography variant="caption2">
                        {t(
                            'stisPublications:associateVehicles.allVehiclesNote',
                            'This publication applies to every vehicle, so the table above is not used. Untick All vehicles to pick individually.'
                        )}
                    </Typography>
                </div>
            )}
        </div>
    );
};
